/* ════════════════════════════════════════════════════════════════════════════
   ManDana — the Project window (singular)
   ---------------------------------------------------------------------------
   Projects, plural, is a window. A project inside it was a PAGE: the writing,
   six properties, and two flat scrolling lists underneath. So the app could
   look at every project through a board, a list and a timeline — with saved
   views, filters, a hand that carries cards — and then, the moment somebody
   stepped INTO one project, the whole grammar disappeared and they got two
   lists.

   This is one project given the same room the many have. Its name on the
   window, its own views, and one board holding everything filed under it: the
   thought captured with no day yet and the hour already booked next Tuesday,
   which are the same work at two ages.

   ── BUILT FROM THE WORKSHOP'S PARTS, NOT FROM A COPY OF THEM ──────────────
   Every surface here comes from `window.MdWs` (md-projects.jsx's foot). Not
   one column, pill, menu row or hand is redrawn. Built from copies, the two
   windows drift one small fix at a time, and the drift is invisible until
   somebody notices the board in one of them still has last month's bug.

   What is NOT shared is the composition, and deliberately: the rows are
   different, the writes are different, the frames underneath are different,
   and three controls are ABSENT rather than disabled. A single shell with an
   `if` per difference reads as neither window.

   ── THE THREE ARE MISSING, NOT DISABLED ───────────────────────────────────
   Inside one project, its project, its pursuit and its domain are constants.
   md-worklist.ts does not have them as fields, so the Filter menu has nothing
   to offer, the Grouping dropdown has one answer and there is no property pill
   to hide. Requirements 4, 5 and 6 are settled by what the catalogue IS. A
   control whose answer can never change is furniture.

   ── EVERY WRITE TO A DAY GOES BACK THROUGH md-app ─────────────────────────
   `dayEvents`, `allDayRecords` and `completed` are React state md-app holds
   and flushes. A second writer reaching for mdStoreWrite would be clobbered by
   the next flush — so a status change travels the road md-app already built
   for onNewBacklog and onOpenDayRecord: the `onRecordStatus` prop. This file
   decides nothing about what a status means; md-record.ts does.
   ════════════════════════════════════════════════════════════════════════════ */

/* eslint-disable no-undef */
(function () {
  const { useState: useStS, useMemo: useMeS, useEffect: useEfS, useRef: useRefS } = React;

  const inkS = (a) => window.ink(a);
  const rgbaS = (c, a) => window.mdRgba(c, a);
  const isLightS = () => window.mdIsLight();
  const domColorS = (id, light) => (window.mdDomainColor ? window.mdDomainColor(id, light) : '#7C68E6');

  /* The same hover/drag washes the Workshop paints, under this window's own
     mark. Copied as CSS rather than shared as a rule because the selectors are
     scoped to a window attribute and a shared sheet would have to name both —
     which is a sheet that silently stops covering the third window. */
  if (typeof document !== 'undefined' && !document.getElementById('md-project-space-styles')) {
    const style = document.createElement('style');
    style.id = 'md-project-space-styles';
    style.textContent = `
[data-md-project-space] [data-md-row]{background:transparent;border-radius:var(--md-radius-control,8px)}
[data-md-project-space] [data-md-row]:hover{background:var(--ws-hover)}
[data-md-project-space] [data-md-row]:focus-visible{background:var(--ws-hover)}
[data-md-project-space] [data-md-rail-row]:hover{background:var(--ws-hover)}
[data-md-project-space] [data-md-card]:hover::after,
[data-md-project-space] [data-md-card]:focus-visible::after{content:'';position:absolute;inset:0;border-radius:inherit;background:var(--ws-hover);pointer-events:none}
[data-md-project-space][data-md-dragging] [data-md-row]:hover,
[data-md-project-space][data-md-dragging] [data-md-rail-row]:hover{background:transparent}
[data-md-project-space][data-md-dragging] [data-md-card]:hover::after{display:none}
[data-md-project-space][data-md-dragging]{cursor:grabbing}
[data-md-project-space] .md-db-menu select{font-size:12.5px;line-height:1.3}
`;
    document.head.append(style);
  }

/* THE SAME CHANNEL THE WORKSHOP USES, and deliberately. The screens this
   window shows underneath its board are the Workshop's own — ProjectView calls
   wsCommit, and wsCommit posts a refusal to `workshop`. A second channel here
   would mean a refusal raised inside this window rendering in the window
   BEHIND it: the person sees nothing happen and no reason why. One product,
   one refusal voice. */
  const PS_NOTICE = 'workshop';
  /* Read off `window` at call time rather than captured at load: this file is a
     plain <script> and md-database.jsx is the one that defines the notifier, so
     a name bound up here would be whatever the load order happened to be. It is
     also worth ONE door — the name was misremembered once, and a refusal that
     goes nowhere is silence, which is the exact failure a refusal exists to
     prevent. One wrong name is a bug; three copies of it is three bugs. */
  const psNotify = (message, undoable = false) => {
    if (window.dbNotify) window.dbNotify(PS_NOTICE, message, undoable);
  };

  /* ⌘Z in THIS window: everything a hand can change from inside it. `md:projects`
     and `md:project:` are here because the project's own page is a frame in this
     window now — a rename, a status, a span or a paragraph written in here has to
     be takeable back in here, and a scope that only covered the records would
     have left the page's own edits reaching for the window underneath.

     `md:recordViews:` is in the list and `md:projectViews` is NOT: the two
     prefixes are deliberately unrelated (see md-worklist.ts), because scopes
     match by prefix and one name would have put this window's views inside the
     Projects window's undo.

     `PS_UNDO_TEXT` is the subset somebody TYPES into — only those may merge into
     a burst, so an index rewritten twice in a second stays two acts. */
  const PS_UNDO_KEYS = ['md:projects', 'md:project:', 'md:projectRecords', 'md:recordViews:',
    'md:events:', 'md:records:', 'md:completed:'];
  const PS_UNDO_TEXT = ['md:project:'];

  function ProjectSpace({ accent, projectId, onClose, events, records, projectRecords, boards,
    completed, todayKey, nowMin, onOpenDayRecord = null, onOpenBacklog = null,
    onComposeBacklog = null,
    onScheduleBacklog = null, onRecordStatus = null, onOpenBoard = null, onNewBoard = null,
    onOpenPursuit = null, onOpenDomain = null, initialFrame = null,
    wikiRecords = [], wikiBacklinksFor = null, onWikiOpen = null,
    onWikiCreate = null, tagOptions = [] }) {
    const MdWs = window.MdWs || {};
    const { WsColumn, WsEmptyRail, WsPill, WsAsk, WsTool, WsAnchor, WsSegment, WsControlRow,
      WsSwitch, WsDirArrow, StatusMark, useWsHand, wsAxisMark, wsBoxPlace, wsMenuPlace,
      wsCommit, wsReorder, wsLanding, wsLandingLine, wsStill, wsDayLabel, wsSpentLabel,
      WS_FIELD_LABEL, WS_COL_WIDTH, WS_COL_MIN, WS_COL_GAP,
      WS_NARROW_RECORD_FIELDS, WS_DISPLAY_DEFAULTS, WS_DANGER, WS_NONE } = MdWs;

    const light = isLightS();
    const L = window.useMdLayout ? window.useMdLayout() : { canvas: false, overlay: true, panel: 0 };
    const shell = useRefS(null);

    const [tick, bump] = useStS(0);
    const again = () => bump((n) => n + 1);

    const resizable = !!L.canvas && !L.overlay;
    /* How you left it (CS-4). This window's default is `full` where the other
       two are `board` — a project is a place you go INTO — and that difference
       is this surface's to keep, which is why the fallback is a parameter. */
    const [wanted, setWanted] = window.useMdRemembered('project', L,
      resizable ? window.MD_SPACE_SIZES : window.MD_SPACE_FULL_ONLY, 'full');
    const size = resizable ? wanted : 'full';

    /* The project's own page — the writing and the six properties — is a frame
       inside this window now, reached from the name at the top. Exactly as
       ProjectView is a frame inside Projects today; it stopped being the
       destination and became a thing you can go and read. */
    const { stack, frame, push, pop } = window.useMdFrames(initialFrame, [initialFrame],
      { ride: 'project-frame' });

    /* Re-read whenever storage has moved, not only when this window moved it.
       A project renamed from the Workshop underneath — or by an undo, or by a
       pull from another device — has to show up on the window wearing its
       name, and `tick` only knows about the presses that happened in here. */
    const project = useMeS(() => (window.mdProjectById ? window.mdProjectById(projectId) : null),
      [projectId, tick, window.mdStorageRevision ? window.mdStorageRevision() : 0]);

    const [menu, setMenu] = useStS(null);
    const [ask, setAsk] = useStS(null);
    const [draft, setDraft] = useStS(null);
    const [editHead, setEditHead] = useStS(false);
    const [armed, setArmed] = useStS(false);
    const [search, setSearch] = useStS('');
    const [searchOpen, setSearchOpen] = useStS(false);

    const views = useMeS(() => (window.mdWorklistViews ? window.mdWorklistViews(projectId) : []),
      [projectId, tick]);
    const [activeId, setActiveId] = useStS(() =>
      (window.mdWorklistActiveId ? window.mdWorklistActiveId(projectId) : ''));
    const view = draft || views.find((row) => row.id === activeId) || views[0];

    /* Which catalogue the shared asking menu is looking at. Without this it
       reaches for the Workshop's fields and offers Pursuit and Domain — the
       two things requirement 5 says are not in here. */
    const lens = useMeS(() => ({
      fields: window.MD_WORKLIST_FIELDS || [],
      facetCounts: (v, c, field) => (window.mdWorklistFacetCounts
        ? window.mdWorklistFacetCounts(v, c, field) : {}),
    }), []);

    const effective = useMeS(() => (view ? { ...view, search: search || view.search } : null),
      [view, search]);

    /* Handed in from the state md-app already holds, plus WHICH project is
       being looked at — the scope the rows are drawn for. */
    const ctx = useMeS(() => ({ events: events || {}, records: records || {},
      boards: boards || [], projectRecords: projectRecords || [], completed: completed || {},
      todayKey, nowMin, projectId }),
    [events, records, boards, projectRecords, completed, todayKey, nowMin, projectId]);

    const groups = useMeS(() => (effective && window.mdWorklistGroups
      ? window.mdWorklistGroups(effective, ctx) : []), [effective, ctx, tick]);
    const total = groups.reduce((sum, group) => sum + group.rows.length, 0);

    const asking = !!view && (view.filters.length > 0 || !!(search || view.search));
    const hidden = useMeS(() => (asking && effective
      ? window.mdWorklistRows({ ...effective, filters: [], search: '' }, ctx).length - total
      : 0), [asking, effective, ctx, tick, total]);

    /* ── the writes ────────────────────────────────────────────────────────
       One door for a status, whatever kind of record it is and whichever
       gesture asked. md-app applies it, because a dated record's word and its
       tick both live in state this window does not own.

       AND A REFUSAL IS A SENTENCE, through the Workshop's own channel (WR-4).
       The door answers `{ ok, reason }`; a caller that only ever said false —
       and the test fakes that still do — is read as a bare refusal, so a
       dropped card never fails in silence. */
    const setStatus = (row, want) => {
      if (!row || !onRecordStatus) return false;
      const said = onRecordStatus(row, want);
      const done = wsCommit(said && typeof said === 'object' ? said : { ok: !!said });
      again();
      return done;
    };

    const patch = (changes) => {
      if (draft) { setDraft({ ...draft, ...changes }); return; }
      if (view) window.mdWorklistViewPatch(projectId, view.id, changes);
      again();
    };

    const chooseView = (id) => {
      setDraft(null); setEditHead(false); setArmed(false);
      setActiveId(id); setSearch('');
      window.mdWorklistSetActive(projectId, id);
      again();
    };

    const startDraft = () => {
      if (draft) return;
      setMenu(null); setAsk(null); setEditHead(false);
      const base = window.mdWorklistSanitiseView({ type: 'list' }) || {};
      setDraft({ ...base, id: 'draft', name: '', description: '' });
    };
    const cancelDraft = () => setDraft(null);
    const saveDraft = () => {
      if (!draft) return;
      const { id: _id, name, description, type, ...rest } = draft;
      const made = window.mdWorklistViewCreate(projectId, type, name || undefined,
        { ...rest, description });
      setDraft(null);
      if (!made) return;
      setActiveId(made.id);
      psNotify('The view is kept.');
      again();
    };
    const deleteView = () => {
      if (views.length <= 1) return;
      window.mdWorklistViewDelete(projectId, view.id);
      setActiveId(window.mdWorklistActiveId(projectId));
      setArmed(false); setEditHead(false); again();
    };

    const clearAsking = () => { setAsk(null); setSearch(''); setSearchOpen(false);
      patch({ filters: [], search: '' }); };

    /* ── the ways in and out ───────────────────────────────────────────────
       One layer on the shared LIFO stack. Escape closes the frame if one is
       open, then the window — and the Projects window underneath keeps its own
       layer, so the next Escape is its own.  */
    const escape = window.useDbEscapeLayer || (() => {});
    escape(() => { if (stack.length) pop(); else if (onClose) onClose(); }, true);
    const searchField = useRefS(null);
    escape(() => { setSearch(''); setSearchOpen(false); },
      searchOpen || search !== '', (target) => target === searchField.current);
    escape(() => { setDraft(null); setEditHead(false); setArmed(false); }, !!draft || editHead);

    /* ⌘Z, armed while this window is open. mdHistoryOpen stacks by open order,
       so opening over the Workshop makes this scope the top one for free — and
       closing hands it back. */
    useEfS(() => {
      if (typeof window.mdHistoryOpen !== 'function') return undefined;
      window.mdHistoryOpen('project-window', { keys: PS_UNDO_KEYS, text: PS_UNDO_TEXT });
      return () => window.mdHistoryClose('project-window');
    }, []);

    useEfS(() => {
      const hear = () => again();
      window.addEventListener('mdhistorychange', hear);
      return () => window.removeEventListener('mdhistorychange', hear);
    }, []);

    /* Walking to the project's page ends whatever was being typed on the last
       one — the same seal the Workshop makes for the same reason. */
    useEfS(() => {
      if (typeof window.mdHistorySeal === 'function') window.mdHistorySeal('project-window');
    }, [frame ? frame.kind : null, frame ? frame.id : null]);

    /* F opens the asking. Both windows hear the key on `window`; the front one
       used to win by silencing the event in the capture phase — an arms race
       any third listener would reopen. Now front-ness is read, not enforced:
       the Workshop's twin declines the key while a Project window stands, so
       neither listener needs to silence the other. */
    useEfS(() => {
      const hear = (event) => {
        if (event.key !== 'f' && event.key !== 'F') return;
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        const target = event.target;
        if (target && target.closest
          && target.closest('input, textarea, select, [contenteditable="true"]')) return;
        if (ask) return;
        const tool = document.querySelector('[data-md-project-space] [aria-label="Filter"]');
        setAsk({ at: -1, field: null, ...(tool ? wsBoxPlace(tool) : { x: 8, y: 48 }) });
        event.preventDefault();
      };
      window.addEventListener('keydown', hear);
      return () => window.removeEventListener('keydown', hear);
    }, [ask]);

    if (window.useDbInertBackground) window.useDbInertBackground(shell, size === 'full');

    const [notice, dismissNotice] = window.useDbNotice
      ? window.useDbNotice(PS_NOTICE) : [null, () => {}];
    const undo = useRefS(null);

    /* ── the hands ─────────────────────────────────────────────────────────
       Declared before the early return below would be reached, because a hook
       that runs on some renders and not others is not a hook. */
    const flatIds = groups.reduce((all, group) => all.concat(group.rows), []).map((row) => row.id);
    const groupOf = (id) => groups.find((group) => group.rows.some((row) => row.id === id));
    const rowOf = (id) => {
      const group = groupOf(id);
      return group ? group.rows.find((row) => row.id === id) : null;
    };

    const listDrop = (movedId, over) => {
      const targetId = over.id;
      if (!targetId) return;
      const order = wsReorder(flatIds, movedId, targetId, over.edge);
      if (view && view.groupBy) {
        const from = groupOf(movedId), to = groupOf(targetId);
        if (from && to && from.id !== to.id) {
          if (!setStatus(rowOf(movedId), to.id === 'empty' ? 'backlog' : to.id)) { again(); return; }
        }
      }
      if (order) patch({ order }); else again();
    };
    const listHand = useWsHand({ canDrag: !!view && view.type === 'list' && !view.sorts.length,
      onDrop: listDrop });
    const railHand = useWsHand({ canDrag: !!view && view.type === 'timeline' && !view.sorts.length,
      onDrop: listDrop });

    const placeLabel = (id) => {
      const found = groups.find((group) => group.id === id);
      return (found && found.label) || '';
    };

    const dropOn = (columnId, id) => {
      const row = rowOf(id);
      if (!row) return false;
      const want = columnId === 'empty' ? 'backlog' : columnId;
      if (row.status === want) return false;
      const before = row.status;
      const landed = setStatus(row, want);
      if (landed) {
        undo.current = { row, value: before };
        psNotify(`Moved to ${placeLabel(columnId)}.`, true);
      }
      return landed;
    };

    const boardDrop = (movedId, over) => {
      const from = groupOf(movedId);
      const target = over.zone;
      if (!from || !target) return false;
      if (target !== from.id) {
        if (target === 'empty') return false;             // a place nothing can be
        const landed = dropOn(target, movedId);
        if (landed && over.id && view && !view.sorts.length) {
          const order = wsReorder(flatIds, movedId, over.id, over.edge);
          if (order) patch({ order });
        }
        return landed;
      }
      if (over.id && view && !view.sorts.length) {
        const order = wsReorder(flatIds, movedId, over.id, over.edge);
        if (order) patch({ order });
      }
      return true;
    };
    const boardHand = useWsHand({ canDrag: !!view && view.type === 'board', onDrop: boardDrop,
      touchHold: true, travel: true });

    /* A window with no project is not a window. Every hook above has already
       run, so this early return is safe and the same on every render. */
    if (!project || !view) return null;

    const color = domColorS(project.domainId, light);
    const DbGlyph = window.DbGlyph;
    const DbMenu = window.DbMenu;
    const DbCommittedInput = window.DbCommittedInput;
    const MDIS = window.MDI || {};

    /* ── opening a record ──────────────────────────────────────────────────
       A dated record opens where it lives — in its day, through md-app's own
       editor. A dateless one opens in the SAME editor (OD-3): the uid goes up
       to md-app and the sheet stands on this window, which stays exactly as
       it was underneath. This window keeps no editor of its own. */
    const openRecord = (id) => {
      const row = rowOf(id);
      if (!row) return;
      if (row.scheduled && onOpenDayRecord) {
        onOpenDayRecord({ ...row.record, day: row.scheduled }); return;
      }
      if (onOpenBacklog) onOpenBacklog(row.uid);
    };

    /* Nothing is minted here (D2). The sheet opens fresh, joined to this
       project and carrying the column's status; nothing is written until the
       sheet is confirmed (P-5), so a sheet closed without creating anything
       leaves this board exactly as it was.

       ── WHICH STATUS IS WORTH NOT SAYING ──────────────────────────────────
       One is: the one a record arrives in when nothing is said at all. Writing
       that is writing what it already has.

       This used to be spelled `status !== 'backlog'`, which reads as the same
       claim and is not. The record workflow has TWO statuses in the backlog
       category — Inbox first, then Backlog — so pressing + on Backlog dropped
       the status, absence resolved to Inbox, and the record landed one column
       from the one that was pressed. The workflow knows its own answer; ask it
       rather than naming a word and hoping. (`empty` is the columns list's
       marker for "no status", not a status.) */
    const inboxStatus = () => {
      if (!window.mdWorkflowForRecord || !window.mdWorkflowInboxOf) return null;
      const flow = window.mdWorkflowForRecord({ pid: projectId });
      const row = flow ? window.mdWorkflowInboxOf(flow.id) : null;
      return row ? row.id : null;
    };
    const newRecord = (status) => {
      if (!onComposeBacklog) return;
      const skip = status === 'empty' || status === inboxStatus();
      onComposeBacklog(projectId, status && !skip ? status : null);
    };

    /* ── the display popover ───────────────────────────────────────────────
       The Workshop's, minus the row it has no field for. There is no "Closed"
       here: a record cannot be closed, and a control offering an answer
       nothing can hold is furniture. */
    const displayMenu = <DbMenu deep onClose={() => setMenu(null)}>
      <WsSegment hook="data-md-viewtype" label="View type" accent={accent} value={view.type}
        options={[{ id: 'board', label: 'Board', glyph: 'board' },
          { id: 'list', label: 'List', glyph: 'list' },
          { id: 'timeline', label: 'Timeline', glyph: 'timeline' }]}
        onChange={(type) => patch({ type })} />
      <WsControlRow label="Grouping">
        <select aria-label="Group by" value={view.groupBy || ''}
          onChange={(e) => patch({ groupBy: e.target.value || null })}
          style={{ width: '100%', margin: 0, height: 30 }}>
          <option value="">No grouping</option>
          {(window.MD_WORKLIST_GROUP_BY || []).map((axis) =>
            <option key={axis} value={axis}>{WS_FIELD_LABEL[axis]}</option>)}
        </select>
        {view.groupBy && <WsDirArrow label="Group direction" direction={view.groupDirection}
          onFlip={() => patch({ groupDirection: view.groupDirection === 'desc' ? 'asc' : 'desc' })} />}
      </WsControlRow>
      <WsControlRow label="Ordering">
        <select aria-label="Order by" value={view.sorts.length ? view.sorts[0].field : ''}
          onChange={(e) => patch({ sorts: e.target.value
            ? [{ field: e.target.value,
              direction: view.sorts.length ? view.sorts[0].direction : 'asc' }]
            : [] })}
          style={{ width: '100%', margin: 0, height: 30 }}>
          <option value="">Manual</option>
          {(window.MD_WORKLIST_FIELDS || []).map((f) =>
            <option key={f} value={f}>{WS_FIELD_LABEL[f]}</option>)}
        </select>
        {view.sorts.length > 0 && <WsDirArrow label="Sort direction"
          direction={view.sorts[0].direction}
          onFlip={() => patch({ sorts: [{ field: view.sorts[0].field,
            direction: view.sorts[0].direction === 'desc' ? 'asc' : 'desc' }] })} />}
      </WsControlRow>
      <div className="md-db-menu-rule" />
      <div className="md-db-field-label">
        {view.type === 'board' ? 'Board options' : view.type === 'timeline' ? 'Timeline options' : 'List options'}
      </div>
      {view.type !== 'timeline' && <WsSwitch
        label={view.type === 'board' ? 'Show empty columns' : 'Show empty groups'}
        on={view.showEmptyGroups}
        onFlip={() => patch({ showEmptyGroups: !view.showEmptyGroups })} />}
      {view.type === 'timeline' && <WsControlRow label="Zoom">
        <select aria-label="Zoom" value={view.timelineZoom}
          onChange={(e) => patch({ timelineZoom: e.target.value })}
          style={{ width: '100%', margin: 0, height: 30 }}>
          {(window.MD_VIEWS_ZOOMS || []).map((zoom) => <option key={zoom} value={zoom}>
            {zoom.charAt(0).toUpperCase() + zoom.slice(1)}</option>)}
        </select>
      </WsControlRow>}
      <div className="md-db-menu-rule" />
      <div className="md-db-field-label">Display properties</div>
      <div data-md-props style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 8px 8px' }}>
        {(window.MD_WORKLIST_FIELDS || []).map((field) => {
          const on = view.visibleFields.indexOf(field) !== -1;
          const locked = field === 'name';
          return <button key={field} type="button" data-md-prop={field}
            aria-pressed={on ? 'true' : 'false'} aria-disabled={locked ? 'true' : undefined}
            title={locked ? 'Always' : undefined}
            onClick={() => { if (locked) return;
              patch({ visibleFields: on ? view.visibleFields.filter((f) => f !== field)
                : [...view.visibleFields, field] }); }}
            style={{ minHeight: 22, border: 'none', borderRadius: 7, padding: '2px 9px',
              cursor: locked ? 'default' : 'pointer', font: 'inherit', fontSize: 10.5,
              fontWeight: 620, opacity: locked ? 0.6 : 1,
              background: on ? rgbaS(accent, light ? 0.12 : 0.16) : inkS(light ? 0.045 : 0.06),
              color: on ? inkS(0.88) : inkS(0.42) }}>
            {WS_FIELD_LABEL[field]}
          </button>;
        })}
      </div>
      <div className="md-db-menu-rule" />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '5px 8px 3px' }}>
        <button type="button" onClick={() => patch({ ...WS_DISPLAY_DEFAULTS(view.type),
          groupBy: view.type === 'board' ? 'status' : null,
          visibleFields: [...(window.MD_WORKLIST_FIELDS || [])] })}
          style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 8px',
            cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 600,
            background: 'transparent', color: inkS(0.5) }}>Reset</button>
        <button type="button" onClick={() => setMenu(null)}
          style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 10px',
            cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
            background: 'transparent', color: rgbaS(accent, 1) }}>Done</button>
      </div>
    </DbMenu>;

    /* ── the list ─────────────────────────────────────────────────────────── */
    const narrow = !L.canvas || L.overlay;
    const kept = view.visibleFields.filter((f) => WS_NARROW_RECORD_FIELDS.includes(f));
    const fields = narrow ? (kept.length ? kept : ['name']) : view.visibleFields;
    const template = fields
      .map((f) => (narrow && f === 'name' ? 'minmax(0,1fr)' : WS_COL_WIDTH[f] || 'minmax(0,1fr)'))
      .join(' ');
    const gridMin = narrow ? 0 : fields.reduce((sum, f) => sum + (WS_COL_MIN[f] || 80), 0)
      + Math.max(0, fields.length - 1) * WS_COL_GAP;

    const sortBy = (field) => {
      const held = view.sorts.find((sort) => sort.field === field);
      if (!held) patch({ sorts: [{ field, direction: 'asc' }] });
      else if (held.direction === 'asc') patch({ sorts: [{ field, direction: 'desc' }] });
      else patch({ sorts: [] });
    };
    const sortState = (field) => {
      const held = view.sorts.find((sort) => sort.field === field);
      return held ? (held.direction === 'asc' ? 'ascending' : 'descending') : 'none';
    };

    /* EVERY RECORD HAS A DAY, so this always answers — but it says WHICH of the
       two kinds of day it is. A date on its own reads as "this is when this
       happens", and printing the day something was captured in that voice would
       tell somebody they had booked a thing they only thought of. One door, so
       the card and the row cannot end up saying different things about the same
       record — which is exactly what happened when the card had this and the
       list column did not. */
    const dayWord = (row) => (row.scheduled
      ? wsDayLabel(row.scheduled, todayKey)
      : `Written ${wsDayLabel(row.day, todayKey)}`);

    const cell = (field, row) => {
      if (field === 'name') return <span dir="auto" style={{ display: 'flex', alignItems: 'center',
        gap: 9, minWidth: 0, color: inkS(0.94), fontSize: 14, fontWeight: 570 }}>
        <span aria-hidden="true" style={{ flex: 'none', width: 7, height: 7, borderRadius: 3,
          background: rgbaS(color, 0.8) }} />
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }}>{row.name}</span>
      </span>;
      if (field === 'status') return <span style={{ display: 'flex', alignItems: 'center', gap: 6,
        color: inkS(0.66), fontSize: 12 }}>
        <StatusMark category={row.statusCategory} color={row.statusColor} size={12} />
        {row.statusName}
      </span>;
      if (field === 'day') return dayWord(row);
      if (field === 'created') return row.created ? wsDayLabel(row.created, todayKey) : WS_NONE;
      if (field === 'time') return wsSpentLabel(row.minutes);
      return WS_NONE;
    };

    const rowLine = (row) => { const land = wsLanding(listHand.drag, row.id);
      return <button key={row.id} type="button" data-md-row data-md-drag-id={row.id}
        data-md-drop-target={land ? '' : undefined}
        data-md-drop-edge={land && listHand.drag.edge ? listHand.drag.edge : undefined}
        onClick={() => { if (listHand.swallowClick()) return; openRecord(row.id); }}
        onPointerDown={(event) => listHand.start(event, row.id)}
        aria-label={[row.name, row.statusName,
          row.scheduled ? wsDayLabel(row.scheduled, todayKey)
            : `written ${wsDayLabel(row.day, todayKey)}, not scheduled`,
          row.minutes ? `${wsSpentLabel(row.minutes)} given` : 'no time yet'].join(' — ')}
        style={{ display: 'grid', gridTemplateColumns: template, alignItems: 'center', gap: 10,
          width: '100%', minHeight: 40, border: 'none', color: inkS(1), textAlign: 'start',
          padding: '7px 6px', cursor: 'pointer', font: 'inherit',
          opacity: listHand.drag && listHand.drag.id === row.id ? 0.45 : undefined,
          boxShadow: land ? wsLandingLine(land, accent) : undefined }}>
        {fields.map((field) => <span key={field} data-md-cell={field}
          dir={field === 'name' ? undefined : 'auto'}
          style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: field === 'name' ? undefined : inkS(0.44),
            fontSize: field === 'name' ? undefined : 12,
            textAlign: field === 'time' ? 'end' : 'start',
            fontVariantNumeric: 'tabular-nums' }}>{cell(field, row)}</span>)}
      </button>; };

    const header = <div role="row" style={{ position: 'sticky', top: 0, zIndex: 2,
      display: 'grid', gridTemplateColumns: template, alignItems: 'center', gap: 10,
      padding: '0 6px', height: 30, background: light ? '#f8f9fa' : '#000',
      borderBottom: `1px solid ${inkS(light ? 0.07 : 0.09)}` }}>
      {fields.map((field) => {
        const state = sortState(field);
        return <button key={field} type="button" data-md-col={field} role="columnheader"
          aria-sort={state} onClick={() => sortBy(field)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, height: '100%',
            border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit',
            fontSize: 10.5, fontWeight: 650, letterSpacing: '.04em', textTransform: 'uppercase',
            color: inkS(state === 'none' ? 0.34 : 0.7),
            justifyContent: field === 'time' ? 'flex-end' : 'flex-start' }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>{WS_FIELD_LABEL[field]}</span>
          {state !== 'none' && <span aria-hidden="true" style={{ flex: 'none', fontSize: 9 }}>
            {state === 'ascending' ? '↑' : '↓'}</span>}
        </button>;
      })}
    </div>;

    /* ── the board ─────────────────────────────────────────────────────────
       THERE IS NO "NOT SAYING" COLUMN, and its absence is the same rule the
       Workshop's domain board keeps: a column you cannot drop onto is a broken
       promise, and one nothing can ever be IN is that promise inverted. Every
       record reads as one of the four — `backlog` is what a record with no day
       and no stored word already says — so the empty bucket here is a place
       nothing can reach. The engine still offers it, because the engine cannot
       know that; this is the catalogue's own answer about its own axis. */
    const axis = view.groupBy;
    const canHold = (id) => id !== 'empty';
    const columns = groups.filter((group) => canHold(group.id) || group.rows.length);
    const cardCell = (field, row) => {
      if (field === 'status') return <span key={field} data-md-card-cell={field}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <StatusMark category={row.statusCategory} color={row.statusColor} size={10} />
        {row.statusName}
      </span>;
      const said = field === 'day' ? dayWord(row)
        : field === 'created' ? (row.created ? wsDayLabel(row.created, todayKey) : null)
        : field === 'time' ? (row.minutes ? wsSpentLabel(row.minutes) : null) : null;
      return said ? <span key={field} data-md-card-cell={field} dir="auto"
        style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flex: 'none' }}>
        {said}</span> : null;
    };
    const cardMeta = (row) => view.visibleFields
      .filter((field) => field !== 'name' && field !== axis)
      .map((field) => cardCell(field, row));

    const board = axis
      ? <div data-md-board className="md-scroll" style={{ flex: 1, minHeight: 0, display: 'flex',
        gap: 9, alignItems: 'stretch', overflowX: 'auto', overflowY: 'hidden',
        padding: '10px 12px 14px' }}>
        {columns.map((group) => <WsColumn key={group.id} group={group} accent={accent}
          mark={wsAxisMark(axis, group, light, accent)} hand={boardHand} meta={cardMeta}
          droppable={canHold(group.id)} placed={!view.sorts.length} noun="record"
          onOpen={openRecord} onNew={newRecord} />)}
      </div>
      : <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
        <span style={{ color: inkS(0.5), fontSize: 13.5 }}>Choose how the columns stack.</span>
        <div style={{ display: 'flex', gap: 7 }}>
          {(window.MD_WORKLIST_GROUP_BY || []).map((option) => <button key={option} type="button"
            aria-label={`Group by ${WS_FIELD_LABEL[option].toLowerCase()}`}
            onClick={() => patch({ groupBy: option })}
            style={{ height: 32, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              padding: '0 13px', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 620,
              background: rgbaS(accent, light ? 0.12 : 0.16), color: inkS(0.86) }}>
            {WS_FIELD_LABEL[option]}</button>)}
        </div>
      </div>;

    /* ── the timeline ──────────────────────────────────────────────────────
       A record occupies ONE day, so its span begins and ends on it — and every
       record has one, because a thing written on Tuesday and never scheduled
       still happened on Tuesday. Nothing is missing from this surface; dragging
       across a row is how a record that was merely written gets a day somebody
       chose. */
    const psTimelineRow = (row) => ({ id: row.id, name: row.name, start: row.day || null,
      target: row.day || null, color, status: row.status, domainId: project.domainId });
    const timelineRows = groups.reduce((all, group) => all.concat(group.rows), [])
      .map(psTimelineRow);
    const timelineSections = view.groupBy ? groups.map((group) => ({
      id: group.id, label: group.label, count: group.rows.length,
      mark: wsAxisMark(view.groupBy, group, light, accent),
      rows: group.rows.map(psTimelineRow),
    })) : null;

    const timeline = window.MdTimeline ? <window.MdTimeline rows={timelineRows} todayKey={todayKey}
      zoom={view.timelineZoom} onZoom={(next) => patch({ timelineZoom: next })}
      rtl={typeof document !== 'undefined' && document.documentElement.dir === 'rtl'}
      accent={accent} onOpen={openRecord}
      /* A RECORD IS ONE DAY WIDE, so a drag across an empty row is not a span
         being drawn — it is a day being named. The day named is the one the
         press landed on; the rest of the sweep is the gesture, not the answer.

         A record that ALREADY has a day is left where it is and told so out
         loud. Moving it means moving it between two days' documents, which is
         md-app's state to change and not this window's, and a gesture that
         silently does nothing is worse than one that is refused. */
      onWrite={(id, span) => {
        const row = rowOf(id);
        if (!row) return;
        if (row.scheduled) {
          psNotify('Open the record to change its day.');
          return;
        }
        if (onScheduleBacklog) { onScheduleBacklog(row.uid, span.start); again(); }
      }}
      sections={timelineSections}
      railDrag={view.sorts.length ? null : {
        start: railHand.start,
        lift: railHand.drag ? railHand.drag.id : null,
        over: railHand.drag ? railHand.drag.over : null,
        edge: railHand.drag ? railHand.drag.edge : null,
        swallow: railHand.swallowClick,
      }}
      empty="Nothing filed under this project yet." /> : null;

    const joinWord = view.conjunction === 'or' ? 'or' : 'and';
    const flipJoin = () => patch({ conjunction: view.conjunction === 'or' ? 'and' : 'or' });

    const body = <>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0, gap: 2,
        padding: '0 8px', flex: 'none', borderBottom: `1px solid ${inkS(light ? 0.07 : 0.09)}` }}>
        <div role="tablist" aria-label="Views"
          style={{ display: 'flex', alignItems: 'center', gap: 3, flex: '1 1 160px', minWidth: 0,
            height: 40, overflowX: 'auto' }}>
          {views.map((row) => {
            const on = row.id === view.id;
            return [<button key={row.id} type="button" role="tab" aria-selected={on ? 'true' : 'false'}
              data-md-view-type={row.type} onClick={() => chooseView(row.id)}
              style={{ height: 29, flex: 'none', border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
                padding: '0 9px', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 620,
                whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5,
                color: inkS(on ? 0.88 : 0.46),
                background: on ? rgbaS(accent, light ? 0.12 : 0.16) : 'transparent',
                boxShadow: on ? `inset 0 -1.5px ${rgbaS(accent, 0.9)}` : 'none' }}>
              {DbGlyph ? <DbGlyph name={row.type} size={13} color={inkS(on ? 0.7 : 0.4)} /> : null}
              {row.name}</button>,
            on && !draft ? <button key={`${row.id}-edit`} type="button" aria-label="Edit view"
              title="Edit view" aria-pressed={editHead ? 'true' : 'false'}
              onClick={() => setEditHead(!editHead)}
              style={{ height: 22, width: 22, flex: 'none', border: 'none', borderRadius: 6,
                cursor: 'pointer', font: 'inherit', fontSize: 11, background: 'transparent',
                color: inkS(editHead ? 0.8 : 0.42) }}>
              <span aria-hidden="true">✎</span></button> : null];
          })}
          {draft && <span data-md-draft style={{ height: 27, flex: 'none', display: 'inline-flex',
            alignItems: 'center', gap: 5, padding: '0 9px', whiteSpace: 'nowrap',
            borderRadius: 'var(--md-radius-control, 8px)', border: `1.5px dashed ${rgbaS(accent, 0.55)}`,
            color: inkS(0.85), fontSize: 12, fontWeight: 620 }}>
            {DbGlyph ? <DbGlyph name={draft.type} size={13} color={inkS(0.6)} /> : null}
            {draft.name.trim() || 'New view'}</span>}
          <WsTool label="New view" active={!!draft} onClick={startDraft}>
            {MDIS.plus ? MDIS.plus(inkS(0.5), 13) : <span aria-hidden="true">+</span>}
          </WsTool>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 40, marginInlineStart: 'auto' }}>
          {searchOpen || search !== '' ? (
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              aria-label="Search records" placeholder="Search" autoFocus ref={searchField}
              onBlur={() => { if (search === '') setSearchOpen(false); }}
              style={{ width: 118, height: 29, flex: 'none', border: 'none',
                borderBottom: `1px solid ${inkS(light ? 0.12 : 0.16)}`, outline: 'none',
                background: 'transparent', color: inkS(0.86), font: 'inherit', fontSize: 12.5,
                padding: '0 3px' }} />
          ) : (
            <WsTool label="Search records" onClick={() => setSearchOpen(true)}>
              {DbGlyph ? <DbGlyph name="search" size={15} /> : null}
            </WsTool>
          )}
          <WsTool label="Filter" active={!!ask || view.filters.length > 0}
            text={view.filters.length ? String(view.filters.length) : null}
            onClick={(event) => setAsk(ask ? null : { at: -1, field: null, ...wsMenuPlace(event) })}>
            {DbGlyph ? <DbGlyph name="filter" size={15} /> : null}
          </WsTool>
          <WsAnchor>
            <WsTool label="Display" active={menu === 'display'}
              onClick={() => setMenu(menu === 'display' ? null : 'display')}>
              {DbGlyph ? <DbGlyph name="sliders" size={15} /> : null}
            </WsTool>
            {menu === 'display' && displayMenu}
          </WsAnchor>
        </div>
      </div>

      {(draft || editHead) && <div data-md-view-head style={{ display: 'flex', alignItems: 'center',
        gap: 9, flexWrap: 'wrap', flex: 'none', padding: '8px 12px 7px',
        borderBottom: `1px solid ${inkS(light ? 0.06 : 0.08)}`,
        animation: wsStill() ? 'none' : 'mdDbSpring .18s cubic-bezier(.22,.8,.25,1)' }}>
        {DbGlyph ? <DbGlyph name={view.type} size={15} color={inkS(0.5)} /> : null}
        {draft ? <>
          <input aria-label="View name" placeholder="New view" autoFocus value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            style={{ height: 30, flex: '0 1 190px', minWidth: 120, border: 'none', outline: 'none',
              borderBottom: `1px solid ${inkS(light ? 0.14 : 0.18)}`, background: 'transparent',
              color: inkS(0.9), font: 'inherit', fontSize: 13, fontWeight: 640, padding: '0 3px' }} />
          <input aria-label="View description" placeholder="Description (optional)"
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            style={{ height: 30, flex: '1 1 210px', minWidth: 150, border: 'none', outline: 'none',
              borderBottom: `1px solid ${inkS(light ? 0.09 : 0.12)}`, background: 'transparent',
              color: inkS(0.72), font: 'inherit', fontSize: 12, padding: '0 3px' }} />
          <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, flex: 'none' }}>
            <button type="button" aria-label="Cancel" onClick={cancelDraft}
              style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 10px',
                cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 600,
                background: 'transparent', color: inkS(0.55) }}>Cancel</button>
            <button type="button" aria-label="Save" onClick={saveDraft}
              style={{ height: 26, border: 'none', borderRadius: 7, padding: '0 12px',
                cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 650,
                background: rgbaS(accent, light ? 0.14 : 0.2), color: inkS(0.9) }}>Save</button>
          </span>
        </> : <>
          <DbCommittedInput aria-label="View name" placeholder="New view" value={view.name}
            commit={(name) => { patch({ name }); return { ok: true }; }}
            style={{ height: 30, flex: '0 1 190px', minWidth: 120, border: 'none', outline: 'none',
              borderBottom: `1px solid ${inkS(light ? 0.14 : 0.18)}`, background: 'transparent',
              color: inkS(0.9), font: 'inherit', fontSize: 13, fontWeight: 640, padding: '0 3px' }} />
          <DbCommittedInput aria-label="View description" placeholder="Description (optional)"
            value={view.description || ''}
            commit={(description) => { patch({ description }); return { ok: true }; }}
            style={{ height: 30, flex: '1 1 210px', minWidth: 150, border: 'none', outline: 'none',
              borderBottom: `1px solid ${inkS(light ? 0.09 : 0.12)}`, background: 'transparent',
              color: inkS(0.72), font: 'inherit', fontSize: 12, padding: '0 3px' }} />
          <span style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center',
            gap: 10, flex: 'none' }}>
            {views.length > 1 && <button type="button" data-md-head-delete
              aria-label={armed ? 'Delete view — press again' : 'Delete view'}
              title={armed ? 'Press again to delete' : 'Delete view'}
              onBlur={() => setArmed(false)}
              onClick={() => { if (!armed) { setArmed(true); return; } deleteView(); }}
              style={{ height: 26, minWidth: 28, border: 'none', borderRadius: 7,
                padding: armed ? '0 9px' : 0, cursor: 'pointer', font: 'inherit',
                fontSize: 11, fontWeight: 620, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flex: 'none',
                background: armed ? rgbaS(WS_DANGER(light), light ? 0.1 : 0.16) : 'transparent',
                color: armed ? WS_DANGER(light) : inkS(0.4) }}>
              {armed ? 'Delete?'
                : (MDIS.trash ? MDIS.trash(inkS(0.4), 14) : <span aria-hidden="true">🗑</span>)}
            </button>}
            <button type="button" data-md-head-done aria-label="Done" title="Done"
              onClick={() => { setArmed(false); setEditHead(false); }}
              style={{ height: 26, minWidth: 28, border: 'none', borderRadius: 7,
                padding: 0, cursor: 'pointer', font: 'inherit', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', flex: 'none',
                background: 'transparent', color: rgbaS(accent, 0.95) }}>
              {MDIS.check ? MDIS.check(rgbaS(accent, 0.95), 15) : <span aria-hidden="true">✓</span>}
            </button>
          </span>
        </>}
      </div>}

      {(view.filters.length > 0 || view.groupBy) &&
        <div className="md-db-chipbar" style={{ display: 'flex', alignItems: 'center', gap: 5,
          flex: 'none', padding: '7px 10px 2px', overflowX: 'auto' }}>
          {view.groupBy && <button type="button" onClick={() => setMenu('display')}
            style={{ height: 25, flex: 'none', border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              padding: '0 8px', cursor: 'pointer', font: 'inherit', fontSize: 10.5, whiteSpace: 'nowrap',
              background: inkS(light ? 0.05 : 0.07), color: inkS(0.7) }}>
            Grouped by {WS_FIELD_LABEL[view.groupBy].toLowerCase()}</button>}
          {view.filters.map((filter, at) => [
            at > 0 && (at === 1
              ? <button key={`j${at}`} type="button" data-md-joint onClick={flipJoin}
                  aria-label="Match all or any" title={joinWord === 'or'
                    ? 'Matching any of these — press for all' : 'Matching all of these — press for any'}
                  style={{ height: 25, flex: 'none', border: 'none', background: 'transparent',
                    padding: '0 2px', cursor: 'pointer', font: 'inherit', fontSize: 10.5,
                    fontStyle: 'italic', color: inkS(0.5), textDecoration: 'underline',
                    textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{joinWord}</button>
              : <span key={`j${at}`} data-md-joint style={{ height: 25, flex: 'none', display: 'flex',
                  alignItems: 'center', padding: '0 2px', fontSize: 10.5, fontStyle: 'italic',
                  color: inkS(0.34) }}>{joinWord}</span>),
            <WsPill key={at} filter={filter} accent={accent}
              onAsk={(box) => setAsk({ at, field: filter.field, ...box })}
              onWrite={(op) => patch({ filters: view.filters.map((row, index) =>
                (index === at ? { ...row, operator: op } : row)) })}
              onRemove={() => { setAsk(null);
                patch({ filters: view.filters.filter((_, index) => index !== at) }); }} />,
          ])}
          {view.filters.length > 0 && <button type="button" data-md-pill-add
            aria-label="Add a filter" title="Add a filter"
            onClick={(event) => setAsk(ask ? null : { at: -1, field: null, ...wsMenuPlace(event) })}
            style={{ height: 25, width: 25, flex: 'none', border: 'none',
              borderRadius: 'var(--md-radius-control, 8px)', cursor: 'pointer', font: 'inherit',
              fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', color: inkS(0.45) }}>
            {MDIS.plus ? MDIS.plus(inkS(0.45), 12) : <span aria-hidden="true">+</span>}
          </button>}
          {view.filters.length > 0 && <span style={{ position: 'sticky', insetInlineEnd: 0,
            marginInlineStart: 'auto', flex: 'none', display: 'flex', alignItems: 'center',
            paddingInlineStart: 8,
            background: light ? 'rgba(248,249,250,0.92)' : 'rgba(16,18,22,0.92)' }}>
            <button type="button" data-md-rail-clear
              aria-label="Clear filters" title="Clear filters" onClick={clearAsking}
              style={{ height: 25, flex: 'none', border: 'none',
                borderRadius: 'var(--md-radius-control, 8px)', padding: '0 9px', cursor: 'pointer',
                font: 'inherit', fontSize: 10.5, fontWeight: 620, whiteSpace: 'nowrap',
                background: inkS(light ? 0.05 : 0.07), color: inkS(0.58) }}>Clear</button>
          </span>}
        </div>}

      {ask && <WsAsk key={`${ask.at}:${ask.field || ''}`} view={effective} ctx={ctx} patch={patch}
        at={ask.at} field={ask.field} x={ask.x} y={ask.y} lens={lens}
        onClose={() => setAsk(null)} />}

      {view.type === 'board' ? board : view.type === 'timeline' ? timeline
        : <div className="md-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto',
          padding: '0 12px 28px' }}>
        <div style={{ minWidth: gridMin }}>
          {header}
          {groups.map((group) => <div key={group.id} style={{ marginTop: view.groupBy ? 14 : 2 }}>
            {view.groupBy && <div data-md-group={group.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 6px 7px' }}>
              {wsAxisMark(view.groupBy, group, light, accent)}
              <span style={{ color: inkS(0.82), fontSize: 12.5, fontWeight: 640 }}>{group.label}</span>
              <span data-md-group-count style={{ color: inkS(0.3), fontSize: 11.5,
                fontVariantNumeric: 'tabular-nums' }}>{group.rows.length}</span>
            </div>}
            {group.rows.map(rowLine)}
          </div>)}
          {!total && <div style={{ padding: '34px 8px', textAlign: 'center', color: inkS(0.42),
            fontSize: 13.5 }}>
            {search || view.filters.length ? 'Nothing here answers that.' : 'Nothing filed here yet.'}
          </div>}
        </div>
      </div>}

      {hidden > 0 && <div data-md-hidden-line style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 8, flex: 'none', padding: '7px 12px 9px',
        borderTop: `1px solid ${inkS(light ? 0.06 : 0.08)}`, color: inkS(0.45), fontSize: 11.5 }}>
        <span dir="auto">{hidden} hidden by the asking</span>
        <button type="button" aria-label="Clear filters" onClick={clearAsking}
          style={{ height: 22, flex: 'none', border: 'none', borderRadius: 7, padding: '0 8px',
            cursor: 'pointer', font: 'inherit', fontSize: 11, fontWeight: 620,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: rgbaS(accent, light ? 0.1 : 0.14), color: inkS(0.75) }}>
          Clear filters<span aria-hidden="true">×</span>
        </button>
      </div>}
    </>;

    /* ── the frame underneath ──────────────────────────────────────────────
       The project's own page, the Workshop's screen reached through the parts
       door rather than rebuilt. It is the ONLY frame now: a backlog record
       used to be one too, and OD-3 retired it — records open in the compose
       sheet, standing on this window, wherever they are opened from. */
    const ProjectView = MdWs.ProjectView;
    const record = frame && <div className="md-scroll"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 16px 32px' }}>
      {/* The pursuit chip and the domain chip are doors, and the pages they
          open belong to the Projects window — which is still standing right
          underneath this one. So they are handed back rather than rebuilt
          here: md-app walks the Workshop to that page and lets this window go,
          which is the honest reading of "open the pursuit this project is in".
          A second copy of the pursuit page would be a second place to fix. */}
      {frame.kind === 'project-page' && ProjectView && <ProjectView id={projectId} ctx={ctx}
        accent={accent} events={events} records={records} projectRecords={projectRecords}
        boards={boards} onBack={pop} onAgain={again}
        onOpenPursuit={onOpenPursuit} onOpenDomain={onOpenDomain}
        onOpenRecord={(row) => onOpenDayRecord && onOpenDayRecord(row)}
        onOpenBacklog={(uid) => onOpenBacklog && onOpenBacklog(uid)}
        onNewBacklog={() => newRecord(null)}
        onNewRecord={() => newRecord(null)}
        onOpenBoard={onOpenBoard} onNewBoard={onNewBoard}
        wikiRecords={wikiRecords} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
        tagOptions={tagOptions} />}
    </div>;

    const space = (
      <div ref={shell} data-md-project-space data-md-size={size}
        data-md-project={projectId}
        data-md-dragging={listHand.drag || boardHand.drag || railHand.drag ? '' : undefined}
        className={light ? 'md-db-light' : ''}
        style={{ position: 'fixed', zIndex: 145, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', '--db-accent': accent, '--ws-hover': inkS(light ? 0.04 : 0.05),
          ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
          ...(window.mdSpaceWindowStyle
            ? window.mdSpaceWindowStyle(size, light, L.panel, resizable)
            : { inset: 0, borderRadius: 0 }) }}>

        {/* THE NAME IS A DOOR. The Workshop's title is a word; this one is the
            project, and pressing it opens the project's own page — the writing
            and the six properties that used to BE this screen. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 12px 8px',
          flexShrink: 0 }}>
          <button type="button" data-md-space-title aria-label={`Open ${project.name}`}
            onClick={() => { if (!frame) push({ kind: 'project-page' }); }}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
              paddingInlineStart: 4, font: 'inherit', color: inkS(1), fontSize: 19, fontWeight: 680,
              textAlign: 'start', maxWidth: '58%', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' }}>{project.name}</button>
          {!frame && <button type="button" data-md-new="" aria-label="New record"
            onClick={() => newRecord(null)}
            style={{ width: 32, height: 32, display: 'grid', placeItems: 'center', flex: 'none',
              border: 'none', background: 'transparent', cursor: 'pointer',
              borderRadius: 'var(--md-radius-control, 8px)', color: rgbaS(accent, 0.95) }}>
            {MDIS.plus ? MDIS.plus(rgbaS(accent, 0.95), 17) : <span aria-hidden="true">+</span>}
          </button>}
          <span style={{ flex: 1 }} />
          {resizable && <button type="button" onClick={() => setWanted(size === 'full' ? 'board' : 'full')}
            aria-label={size === 'full' ? 'Shrink to the board' : 'Expand to full screen'}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', width: 32,
              height: 32, display: 'grid', placeItems: 'center', color: inkS(0.55) }}>
            {DbGlyph ? <DbGlyph name={size === 'full' ? 'shrink' : 'expand'} size={16} /> : null}
          </button>}
          <window.MdDismiss onClick={onClose} label={`Close ${project.name}`}
            style={{ marginInlineEnd: -4 }} />
        </div>

        {frame ? record : body}

        {notice && <div data-md-notice role="status"
          style={{ position: 'absolute', zIndex: 85, bottom: 14, insetInline: 0, margin: '0 auto',
            width: 'fit-content', maxWidth: 'calc(100% - 28px)', display: 'flex', alignItems: 'center',
            gap: 10, padding: '9px 13px', borderRadius: 'var(--md-radius-surface, 12px)',
            border: `1px solid ${notice.undoable ? rgbaS(accent, 0.3) : inkS(light ? 0.1 : 0.14)}`,
            background: light ? 'rgba(255,255,255,.97)' : 'rgba(28,30,38,.96)',
            boxShadow: '0 10px 28px rgba(0,0,0,.28)', color: inkS(0.86), fontSize: 11.5,
            animation: 'mdDbSpring .22s cubic-bezier(.22,.8,.25,1)' }}>
          <span>{notice.message}</span>
          {notice.undoable && undo.current && <button type="button"
            onClick={() => { const held = undo.current; undo.current = null; dismissNotice();
              if (held) setStatus(held.row, held.value); }}
            style={{ height: 22, flex: 'none', border: 'none', borderRadius: 7, padding: '0 9px',
              cursor: 'pointer', font: 'inherit', fontSize: 10.5, fontWeight: 650,
              background: rgbaS(accent, light ? 0.14 : 0.22), color: inkS(0.9) }}>Undo</button>}
        </div>}

        {listHand.carried}{boardHand.carried}{railHand.carried}
      </div>);

    return ReactDOM.createPortal(space, document.body);
  }

  Object.assign(window, { ProjectSpace });
})();
